import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectDirectionality } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NotesActions } from '@src/suite/common/store/notes/notes.actions';
import { selectSelectedNote } from '@src/suite/common/store/notes/notes.selectors';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import { LoaderComponent } from '@src/suite/loader/loader';
import { ExternalActionsOverlayComponent } from '@src/suite/playbook/components/external-actions-overlay/external-actions-overlay';

import { RawContentEditorComponent } from '../../controls/st-raw-content-editor/st-raw-content-editor';
import { StatusComponent } from '../../controls/status/status';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { selectCurrentStoryParentTopic } from '../../store/fullscreen';
import { getTopicSecurityPrivsWrite } from '../../utils/record-utils';

@Component({
    selector: 'st-note-full-screen',
    imports: [A11yModule, ClickStopPropagationDirective, ExternalActionsOverlayComponent, LoaderComponent, MatButtonModule, MatRippleModule, RawContentEditorComponent, StatusComponent],
    templateUrl: './note-full-screen.html',
    styleUrl: './note-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-note-full-screen',
    },
})
export class NoteFullScreenComponent {
    // injectables
    store = inject(Store);
    dialogRef = inject(MatDialogRef<NoteFullScreenComponent>);
    globalTablesService = inject(GlobalTablesService);
    data = inject<{ createNoteTemplate: boolean }>(MAT_DIALOG_DATA);

    // properties
    PSTFieldNames = PSTFieldNames;
    localRecordChanged = false;

    languageSettings = this.store.selectSignal(selectI18N);

    // computeds

    readonly currentStoryWordsCount = computed(() => {
        const parsedStoryText = parseXml(this.record()?.RawContent, 'text/html');
        let count = 0;
        if (parsedStoryText.body) {
            const bodyChildren = parsedStoryText.body.children;
            for (const child of bodyChildren) {
                if (child?.tagName.toUpperCase() !== 'DIV') {
                    count += child?.textContent
                        ?.replace(/,|;|:/g, ' ')
                        ?.replace(/\s\s+/g, ' ')
                        ?.split(/\s+/)
                        ?.map(val => val.trim())
                        ?.filter(val => !!val).length;
                }
            }
        }
        return `${count || 0} ${this.languageSettings().PSTWebWordsCount}`;
    });

    readonly record = computed(() => {
        return this.store.selectSignal(selectSelectedNote)();
    });
    readonly dir = computed(() => {
        return this.store.selectSignal(selectDirectionality)();
    });
    readonly loading = computed(() => {
        return false;
    });
    readonly noteContent = computed(() => {
        return this.localRecord()?.RawContent ?? '';
    });
    readonly headerLabel = computed(() => {
        if (this.data?.createNoteTemplate) {
            return this.languageSettings().PSTWebCreateNoteTemplateLabel;
        }
        return this.record()?.isNewRecord ? this.languageSettings().PSTWebCreateNoteTitle : this.languageSettings().PSTWebUpdateNoteTitle;
    });
    readonly saveBtnLabel = computed(() => {
        if (this.data?.createNoteTemplate) {
            return this.languageSettings().PSTWebSaveAsTemplateButtonLabel;
        }
        return this.record()?.isNewRecord ? this.languageSettings().PSTWebSaveAndCloseButtonLabel : this.languageSettings().PSTWebSaveChangesNoteLabel;
    });

    readonly disabled = computed(() => {
        const fullscreenRecord = this.store.selectSignal(selectCurrentStoryParentTopic);
        return !getTopicSecurityPrivsWrite(fullscreenRecord()) && !fullscreenRecord()?.Path?.isWasteBinPath;
    });

    // signals
    readonly localRecord = signal<StorytellingNoteRecord>(null);

    // effects
    locarRecordEffect = effect(() => {
        if (this.localRecordChanged) {
            return;
        }
        const record = this.record();
        this.localRecord.set(record ? { ...record } : null);
    });

    // methods
    updateCurrentNote(update: { key: string; value: unknown }) {
        this.localRecord.update(record => {
            if (!record) {
                return record;
            }
            const updatedRecord = { ...record };
            if (update.key === PSTFieldNames.PSTNoteStatus) {
                const noteStatus = update.value as LookupValue;
                updatedRecord[update.key] = { id: noteStatus.id, title: noteStatus.title };
            } else {
                updatedRecord[update.key] = update.value;
            }
            this.localRecordChanged = true;
            return updatedRecord;
        });
        if (update.key !== PSTFieldNames.RawContent && !this.localRecord().isNewRecord) {
            this.store.dispatch(
                NotesActions.updateCurrentNote({
                    record: this.localRecord(),
                    fieldsToUpdate: [{ key: update.key, value: update.value }],
                })
            );
        }
    }
    addOrUpdate() {
        if (this.data?.createNoteTemplate) {
            this.store.dispatch(NotesActions.saveNoteTemplate({ record: this.localRecord() }));
        } else {
            this.store.dispatch(
                NotesActions.updateCurrentNote({
                    record: this.localRecord(),
                    fieldsToUpdate: [{ key: PSTFieldNames.RawContent, value: this.localRecord()?.RawContent }],
                })
            );
        }
        this.close();
    }
    cancel() {
        this.close();
    }
    close() {
        this.dialogRef?.close();
    }
}
